import React, { useMemo } from "react";
import { Clock } from "lucide-react";
import { MobileJobCard } from "./MobileJobCard";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Monday–Sunday bounds of the week containing `d` (local time).
export function getWeekBounds(d) {
  const day = d.getDay(); // 0=Sun..6=Sat
  const monday = new Date(d);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() + (day === 0 ? -6 : 1 - day));
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { start: monday.getTime(), end: sunday.getTime(), startStr: ymd(monday), endStr: ymd(sunday) };
}

/**
 * Completed/Canceled job history with a From/To date filter — shared by
 * MobileHome's History tab (own jobs for a tech, all jobs for a manager)
 * and the standalone /history mobile page.
 */
export default function HistoryTab({ jobs, from, to, onFromChange, onToChange, onOpenJob, testid = "mobile-history" }) {
  const historyJobs = useMemo(() => {
    const fromMs = from ? new Date(`${from}T00:00:00`).getTime() : null;
    const toMs = to ? new Date(`${to}T23:59:59.999`).getTime() : null;
    return (jobs || [])
      .filter((j) => ["Completed", "Canceled"].includes(j.status))
      .filter((j) => {
        if (!fromMs && !toMs) return true;
        const t = j.start ? new Date(j.start).getTime() : 0;
        if (!t) return false;
        if (fromMs && t < fromMs) return false;
        if (toMs && t > toMs) return false;
        return true;
      })
      .sort((a, b) => (b.start || "").localeCompare(a.start || ""));
  }, [jobs, from, to]);

  return (
    <div data-testid={testid} className="pt-2">
      <div className="mx-4 mb-3 bg-white rounded-2xl p-3 flex items-center gap-2 border border-slate-200" data-testid="history-date-filter">
        <div className="flex-1">
          <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">From date</label>
          <input
            type="date"
            value={from}
            onChange={(e) => onFromChange(e.target.value)}
            max={to || undefined}
            data-testid="history-from"
            className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          />
        </div>
        <div className="flex-1">
          <label className="block text-[10.5px] font-bold uppercase tracking-wider text-slate-500 mb-1">To date</label>
          <input
            type="date"
            value={to}
            onChange={(e) => onToChange(e.target.value)}
            min={from || undefined}
            data-testid="history-to"
            className="w-full h-9 px-2 rounded-md border border-slate-300 bg-white text-[13px] text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
          />
        </div>
        {(from || to) && (
          <button
            onClick={() => { onFromChange(""); onToChange(""); }}
            data-testid="history-clear-dates"
            className="self-end h-9 px-3 rounded-md text-[11.5px] font-semibold text-blue-600 hover:bg-blue-50"
          >
            Clear
          </button>
        )}
      </div>
      {historyJobs.length === 0 ? (
        <div className="flex flex-col items-center justify-center px-4 text-center py-14" data-testid={`${testid}-empty`}>
          <Clock className="w-12 h-12 text-slate-300 mb-3" strokeWidth={1.75} />
          <div className="text-base font-bold text-slate-700">No history yet</div>
          <div className="text-sm text-slate-500 mt-1 max-w-[260px]">
            {(from || to) ? "No completed jobs in this date range." : "Completed and canceled jobs show here."}
          </div>
        </div>
      ) : (
        historyJobs.map((j) => (
          <MobileJobCard key={j.id} job={j} showDate onOpen={() => onOpenJob(j.id)} />
        ))
      )}
    </div>
  );
}
